// MF-568 — Card 'pat-next-learning': ability groups and the near-level skills to teach next. Default view: one row per
// group with candidate skills and the school's shared teaching approach. A "Skills" multi-select narrows the skill
// columns while KEEPING every ability group. A skill area with no near item shows the gap, never an easier target.
import { useMemo, useState } from 'react'
import { Box, Chip, Typography } from '@mui/material'
import { INK, FONT, SUMMARY_PALETTE, TONE_PILL } from '../boardTheme'
import { GAP_TEXT, buildNextLearning, fmtD } from './patSkillsShared'

const th = {
  textAlign: 'left',
  fontSize: 11.5,
  fontWeight: 600,
  color: SUMMARY_PALETTE.muted,
  p: 1,
  borderBottom: `1px solid ${INK.axis}`,
  whiteSpace: 'nowrap',
  bgcolor: '#fff',
  position: 'sticky',
  top: 0,
  zIndex: 1
}
const td = {
  verticalAlign: 'top',
  fontSize: 12.5,
  color: SUMMARY_PALETTE.ink,
  p: 1,
  borderBottom: `1px solid ${SUMMARY_PALETTE.grid}`
}

// A wide group rarely has one item near everybody: say how many members this one is near
const coverage = (row) =>
  Number.isFinite(Number(row?.covers)) && Number(row.covers) < Number(row.group_size)
    ? `near ${row.covers} of ${row.group_size} students`
    : null

const Target = ({ row }) =>
  row?.item_uid ? (
    <Box component='li' sx={{ mb: 0.5 }}>
      {row.description || row.item_uid}{' '}
      <Box component='span' sx={{ color: SUMMARY_PALETTE.muted }}>
        (difficulty {fmtD(row.difficulty)}
        {coverage(row) ? ` · ${coverage(row)}` : ''})
      </Box>
    </Box>
  ) : null

const Gap = ({ strand, showStrand }) => (
  <Box
    component='li'
    sx={{
      mb: 0.5,
      listStyle: 'none',
      ml: -2,
      px: 1,
      py: 0.5,
      borderRadius: '6px',
      bgcolor: TONE_PILL.negative.bg,
      color: TONE_PILL.negative.fg,
      fontSize: 12
    }}
  >
    {showStrand ? <b>{strand}: </b> : null}
    {GAP_TEXT}
  </Box>
)

const PatNextLearningCard = ({ data }) => {
  const w = data?.meta?.card?.options?.itemWindow
  const { strands, groups } = useMemo(() => buildNextLearning(data?.rows || []), [data?.rows])
  const [picked, setPicked] = useState([])
  const selected = strands.filter((s) => picked.includes(s))
  const filtered = selected.length > 0
  const toggle = (s) => setPicked((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]))

  return (
    <Box sx={{ height: '100%', minWidth: 0, overflow: 'auto', fontFamily: FONT }}>
      {strands.length > 1 ? (
        <Box
          role='group'
          aria-label='Skills filter'
          sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mb: 1.5 }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: SUMMARY_PALETTE.muted, mr: 0.5 }}>
            Skills
          </Typography>
          {strands.map((s) => (
            <Chip
              key={s}
              size='small'
              label={s}
              clickable
              aria-pressed={picked.includes(s)}
              onClick={() => toggle(s)}
              sx={{
                fontSize: 12,
                bgcolor: picked.includes(s) ? SUMMARY_PALETTE.good : '#eef2f7',
                color: picked.includes(s) ? '#fff' : SUMMARY_PALETTE.ink,
                '&:hover': { bgcolor: picked.includes(s) ? SUMMARY_PALETTE.good : '#e1e8f1' }
              }}
            />
          ))}
          {filtered ? (
            <Chip
              size='small'
              variant='outlined'
              label='Clear'
              onClick={() => setPicked([])}
              sx={{ fontSize: 12 }}
            />
          ) : null}
        </Box>
      ) : null}

      <Box sx={{ overflow: 'auto', border: `1px solid ${SUMMARY_PALETTE.cardBorder}`, borderRadius: '8px' }}>
        <Box
          component='table'
          sx={{
            borderCollapse: 'separate',
            borderSpacing: 0,
            width: '100%',
            minWidth: filtered ? 200 + selected.length * 200 : 640
          }}
        >
          <thead>
            <tr>
              <Box component='th' sx={th}>
                Ability group
              </Box>
              {filtered ? (
                selected.map((s) => (
                  <Box key={s} component='th' sx={th}>
                    {s}
                  </Box>
                ))
              ) : (
                <>
                  <Box component='th' sx={th}>
                    Students
                  </Box>
                  <Box component='th' sx={th}>
                    Candidate skills to learn next
                  </Box>
                  <Box component='th' sx={th}>
                    Shared teaching approach
                  </Box>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <tr key={g.group_no}>
                <Box component='td' sx={{ ...td, whiteSpace: 'nowrap' }}>
                  <b>Group {g.group_no}</b>
                  <Box sx={{ color: SUMMARY_PALETTE.muted, fontSize: 11.5 }}>
                    Scores {fmtD(g.min)}–{fmtD(g.max)}
                  </Box>
                  <Box sx={{ color: SUMMARY_PALETTE.muted, fontSize: 11.5 }}>
                    {g.size} {Number(g.size) === 1 ? 'student' : 'students'}
                  </Box>
                </Box>
                {filtered ? (
                  selected.map((s) => {
                    const c = g.cells.get(s)
                    return (
                      <Box key={s} component='td' sx={td}>
                        {c?.item_uid ? (
                          <>
                            <div>{c.description || c.item_uid}</div>
                            <Box sx={{ color: SUMMARY_PALETTE.muted, fontSize: 11.5 }}>
                              difficulty {fmtD(c.difficulty)}
                              {coverage(c) ? ` · ${coverage(c)}` : ''}
                            </Box>
                          </>
                        ) : c ? (
                          <Box
                            sx={{
                              px: 1,
                              py: 0.5,
                              borderRadius: '6px',
                              bgcolor: TONE_PILL.negative.bg,
                              color: TONE_PILL.negative.fg,
                              fontSize: 12
                            }}
                          >
                            {GAP_TEXT}
                          </Box>
                        ) : (
                          '–'
                        )}
                      </Box>
                    )
                  })
                ) : (
                  <>
                    <Box component='td' sx={{ ...td, color: SUMMARY_PALETTE.muted, maxWidth: 220 }}>
                      {g.students}
                    </Box>
                    <Box component='td' sx={td}>
                      <Box component='ul' sx={{ m: 0, pl: 2 }}>
                        {strands.map((s) => {
                          const c = g.cells.get(s)
                          if (!c) return null
                          return c.item_uid ? (
                            <Target key={s} row={c} />
                          ) : (
                            <Gap key={s} strand={s} showStrand />
                          )
                        })}
                      </Box>
                    </Box>
                    <Box component='td' sx={td}>
                      {g.notes.map((n) => (
                        <Box key={n} sx={{ mb: 0.5 }}>
                          {n}
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </Box>
      </Box>

      <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted, mt: 1, lineHeight: 1.5 }}>
        Every child has a next step. A correct test response calls for a transfer check or a more demanding
        task; it does not remove the student from next-learning planning. Score ± {w ?? 'the'} window is an
        illustrative window, not a validated readiness rule. Teachers confirm prerequisites, refine targets
        and adjust groups using response and classroom evidence.
      </Typography>
    </Box>
  )
}

export default PatNextLearningCard
